import { Component, computed, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { XEMEX_OID } from '@core/constants';
import {
  DeviceResponseModel,
  LocationResponseModel,
  MeterRole,
  PartnerModel,
  RoleModel,
  PartnerUsersResponseModel,
  WhiteLabelUpdateModel,
  PartnerUserModel,
  getUserActivePartner,
  getUserRole,
} from '@core/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CompanyStore } from '@root/app/store/company.store';
import { TabBarComponent, PageBodyComponent } from '@custom-components';
import { MyCompanyGeneralTabComponent } from '../../components/my-company-general-tab/my-company-general-tab.component';
import { MyCompanyUsersTabComponent } from '../../components/my-company-users-tab/my-company-users-tab.component';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { UserStore } from '@root/app/store/user.store';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MY_COMPANY_TABS, MY_COMPANY_USERS_PAGE_SIZE } from '@core/constants/my-company.constant';
import { UserDialogComponent } from '@root/app/pages/companies/components';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { AuthStore } from '@root/app/store/auth.store';
import { DeleteUserDialogComponent } from '@root/app/pages/companies/components/delete-user-dialog/delete-user-dialog.component';
import { ResendInviteDialogComponent } from '@root/app/pages/companies/components/resend-invite-dialog/resend-invite-dialog.component';

@Component({
  selector: 'xemex-company-detail',
  imports: [
    MatButtonModule,
    TabBarComponent,
    PageBodyComponent,
    TranslateModule,
    PageHeaderComponent,
    MatSidenavModule,
    MyCompanyGeneralTabComponent,
    MyCompanyUsersTabComponent,
  ],
  templateUrl: './my-company-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './my-company-page.component.scss',
})
export class MyCompanyPageComponent {
  readonly companyStore = inject(CompanyStore);
  readonly userStore = inject(UserStore);
  readonly authStore = inject(AuthStore);

  readonly companyName = computed(
    () => getUserActivePartner(this.userStore.user())?.partnerName ?? '',
  );

  /** True when the current user is a Xemex user (active partner is the Xemex OID). Xemex isn't a
   * partner, so the general tab hides the settings that only make sense for a real partner
   * (RDT-1019). */
  readonly isXemex = computed(
    () => getUserActivePartner(this.userStore.user())?.partnerOid === XEMEX_OID,
  );

  companyDetail: PartnerModel | undefined;
  whiteLabelValues: any;
  isLoadingWhiteLabel = false;
  deviceRes: DeviceResponseModel | undefined;
  locationRes: LocationResponseModel | undefined;
  roles: RoleModel[] = [];
  users: PartnerUsersResponseModel = { numberOfItems: 0, users: [] };

  MY_COMPANY_TABS = MY_COMPANY_TABS;
  selectedTab = 'general';
  pagePrefix = 'my-company';

  onTabChange(route: string) {
    this.selectedTab = route;
  }
  partnerOid = '';
  private loadedForPartner = '';

  private readonly snackBar = inject(MatSnackBar);
  private readonly translateService = inject(TranslateService);

  constructor(
    private readonly dialog: MatDialog,
    private readonly route: ActivatedRoute,
  ) {
    const tabParam = this.route.snapshot.paramMap.get('tab');
    if (tabParam) {
      this.selectedTab = tabParam;
    }

    // Load the partner-scoped data this page needs once the user (and therefore
    // the partner) is known. Previously this ran eagerly at app bootstrap for
    // every user; now it only happens when My Company is actually opened.
    effect(() => {
      const activePartner = getUserActivePartner(this.userStore.user());
      const partnerOid = activePartner?.partnerOid;
      if (partnerOid && partnerOid !== this.loadedForPartner) {
        this.loadedForPartner = partnerOid;
        if (partnerOid !== XEMEX_OID) {
          this.companyStore.getMyCompanyMethod(partnerOid);
        }
        this.companyStore.getMyCompanyUsers({ partnerOid });
        this.userStore.getRolesMethod();
      }
    });

    effect(() => {
      const activePartner = getUserActivePartner(this.userStore.user());
      this.partnerOid = activePartner?.partnerOid ?? '';
      this.companyDetail = this.companyStore.myCompany();
      this.users = this.companyStore.usersByMyCompany();
      this.isLoadingWhiteLabel = this.companyStore.isLoadingWhiteLabel();
      this.whiteLabelValues = this.companyStore.whiteLabelValue();
    });

    effect(() => {
      this.roles = this.userStore.roleData().roles ?? [];
    });

    effect(() => {
      if (this.companyStore.errorSavingDefaultMeterRole()) {
        this.snackBar.open(
          this.translateService.instant('companies.detailTabs.general.defaultMeterRole.saveError'),
          '',
          { horizontalPosition: 'right', verticalPosition: 'top', duration: 3000 },
        );
      }
    });
  }

  onUpdateWhiteLabel(data: WhiteLabelUpdateModel) {
    if (this.companyDetail?.partnerOid) {
      this.companyStore.updateWhiteLabelMethod({
        partnerOid: this.companyDetail?.partnerOid,
        whiteLabel: data,
      });
    }
  }

  onUpdateDefaultMeterRole(meterRole: MeterRole) {
    if (this.companyDetail?.partnerOid) {
      this.companyStore.updateDefaultMeterRoleMethod({
        partnerOid: this.companyDetail.partnerOid,
        meterRole,
      });
    }
  }

  onUserPageChange(event: { pageIndex: number; pageSize: number }) {
    this.companyStore.getMyCompanyUsers({
      partnerOid: this.partnerOid,
      limit: event.pageSize,
      offset: event.pageIndex,
    });
  }

  userRole(): string | undefined {
    return getUserRole(this.userStore.user())?.roleName;
  }

  permissions(): string[] {
    const partnerOid = this.companyDetail?.partnerOid?.toLowerCase();
    const permissions = this.authStore.permissions();
    return (
      permissions
        ?.filter(
          p =>
            p.groupObjectId.toLowerCase() === partnerOid ||
            p.groupObjectId.toLowerCase() === XEMEX_OID.toLowerCase(),
        )
        .flatMap(p => p.roles.flatMap(role => role.features.flatMap(f => f.features)))
        .filter(f => f.featureEnabled)
        .map(f => f.featureName) ?? []
    );
  }

  onCreateUserClicked() {
    this.dialog
      .open(UserDialogComponent, {
        width: '48rem',
        maxWidth: 'calc(100vw - 2rem)',
        maxHeight: 'calc(100vh - 2rem)',
        data: {
          mode: 'create',
          partnerOid: this.companyDetail?.partnerOid,
          roles: this.roles,
        },
      })
      .afterClosed()
      .subscribe(() => {
        this.companyStore.getMyCompanyUsers({
          partnerOid: this.partnerOid,
          limit: MY_COMPANY_USERS_PAGE_SIZE,
        });
      });
  }

  onEditUserClicked(user: PartnerUserModel) {
    this.dialog
      .open(UserDialogComponent, {
        width: '48rem',
        maxWidth: 'calc(100vw - 2rem)',
        maxHeight: 'calc(100vh - 2rem)',
        data: {
          mode: 'edit',
          partnerOid: this.companyDetail?.partnerOid,
          roles: this.roles,
          user: user,
        },
      })
      .afterClosed()
      .subscribe(() => {
        this.companyStore.getMyCompanyUsers({
          partnerOid: this.partnerOid,
          limit: MY_COMPANY_USERS_PAGE_SIZE,
        });
      });
  }
  onDeleteUserClicked(userOid: string) {
    const currentUserOid = this.authStore.oid();

    this.dialog
      .open(DeleteUserDialogComponent, {
        width: '640px',
        maxWidth: '640px',
        height: '220px',
        data: {
          partnerOid: this.companyDetail?.partnerOid,
          userOid: userOid,
          currentUserOid: currentUserOid,
        },
      })
      .afterClosed()
      .subscribe(() => {
        this.companyStore.getMyCompanyUsers({
          partnerOid: this.partnerOid,
          limit: MY_COMPANY_USERS_PAGE_SIZE,
        });
      });
  }
  onResendInvite(userOid: string) {
    this.dialog.open(ResendInviteDialogComponent, {
      width: '640px',
      maxWidth: '640px',
      height: '220px',
      data: {
        userOid: userOid,
      },
    });
  }
}
